import type {
  QueryFunctionContext,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import groq from 'groq';
import {
  BANNER,
  BENEFITS_PAGE_ITEM,
  querySingletonDoc,
  REBUS_HERO,
  REVIEWS_CAROUSEL,
  RICH_TEXT,
  SEO,
} from 'lib/queries';
import { client } from 'lib/sanity';
import type { BenefitsPage } from 'types/cms/pages/BenefitsPage';

type BenefitsPageQuery = UseQueryResult & {
  data: BenefitsPage;
};

export const BENEFITS_PAGE_KEY = 'benefitsPage';

export const BENEFITS_PAGE_REFS = {
  supplements: {
    documentType: 'timeline_benefitsPage',
    documentId: 'benefits_page',
  },
  skincare: {
    documentType: 'timeline_skincareBenefitsPage',
    documentId: 'skincare_benefits_page',
  },
} as const;

export type BenefitsPageRef =
  (typeof BENEFITS_PAGE_REFS)[keyof typeof BENEFITS_PAGE_REFS];

type BenefitsPageQueryKey = [
  typeof BENEFITS_PAGE_KEY,
  BenefitsPageRef['documentType'],
  BenefitsPageRef['documentId'],
];

export const fetchBenefitsPage = async ({
  queryKey,
}: QueryFunctionContext<BenefitsPageQueryKey>) => {
  const [, documentType, documentId] = queryKey;
  const query = groq`
    ${querySingletonDoc(documentType, documentId)} {
      ${SEO},
      ${REBUS_HERO},
      introduction[] {
        ${RICH_TEXT}
      },
      reviews[]-> {
        ${REVIEWS_CAROUSEL}
      },
      banner {
        ${BANNER}
      },
      benefits[] {
        ${BENEFITS_PAGE_ITEM}
      }

    }
  `;

  const response: BenefitsPage = await client.fetch(query);
  return response;
};

export const useBenefitsPage = (
  ref: BenefitsPageRef = BENEFITS_PAGE_REFS.supplements,
) =>
  useQuery({
    queryKey: [BENEFITS_PAGE_KEY, ref.documentType, ref.documentId],
    queryFn: fetchBenefitsPage,
  }) as BenefitsPageQuery;
